/**
 * Ödeme yolunun seçimi ve beklenen ödeme durumlarının tanınması.
 *
 * NEDEN VAR (GUNUM-VAR-1X, 3 Ekim 2026): Premium sayfası Google Play yolunu
 * `'getDigitalGoodsService' in window` ile seçiyordu. Edge gibi Chromium
 * tarayıcılarda bu işlev Play Store'dan kurulmuş TWA olmadan da tanımlı;
 * çağrı "unsupported payment method" ile reddediliyor ve web kullanıcısı
 * çalışmayan bir satın alma düğmesiyle kalıyordu.
 *
 * Kural: yol, platform KESİNLEŞİNCE seçilir.
 *   - Native uygulama (iOS + Android) → RevenueCat
 *   - Play Store'dan açılmış TWA     → Google Play (yine de servis hazır mı sorulur)
 *   - Diğer her şey                 → web: satın alma yok, mağaza bağlantısı var
 */

export type BillingPlatform = 'revenuecat' | 'google_play' | 'web'

export interface BillingEnvironment {
  isNative: boolean
  isTwa: boolean
}

export function chooseBillingPlatform({ isNative, isTwa }: BillingEnvironment): BillingPlatform {
  if (isNative) return 'revenuecat'
  if (isTwa) return 'google_play'
  return 'web'
}

/** TWA paketinin kimliği (`twa/twa-manifest.json` → packageId). */
const TWA_REFERRER = 'android-app://com.gunumvar.app'
const TWA_SESSION_KEY = 'gv_twa_session'

interface SessionLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

/**
 * TWA, uygulamayı açarken `document.referrer`'ı `android-app://<paket>` yapar.
 * Yönlendiren yalnız ilk yüklemede gelir; sayfa yenilenince kaybolmasın diye
 * oturum belleğinde işaretlenir.
 */
export function isTrustedWebActivity(referrer: string, session: SessionLike): boolean {
  if (referrer.startsWith(TWA_REFERRER)) {
    try {
      session.setItem(TWA_SESSION_KEY, '1')
    } catch {
      // Oturum belleği kapalıysa işaretlenemez; bu açılış için yine TWA'dır.
    }
    return true
  }
  try {
    return session.getItem(TWA_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

/** Tarayıcı ortamında TWA mı? Test dışındaki çağrılar için kısa yol. */
export function detectTrustedWebActivity(): boolean {
  if (typeof document === 'undefined' || typeof sessionStorage === 'undefined') return false
  return isTrustedWebActivity(document.referrer || '', sessionStorage)
}

interface ErrorLike {
  name?: unknown
  code?: unknown
  message?: unknown
  userCancelled?: unknown
}

function asErrorLike(error: unknown): ErrorLike | null {
  return typeof error === 'object' && error !== null ? (error as ErrorLike) : null
}

/**
 * RevenueCat `PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR` değeri `'1'`.
 * `'PURCHASE_CANCELLED'` eski kodun aradığı metin; zararsız olduğu için tanınmaya devam eder.
 */
const RC_PURCHASE_CANCELLED_CODES = ['1', 'PURCHASE_CANCELLED']

/**
 * Kullanıcı satın almayı kendisi mi iptal etti?
 * RevenueCat: `userCancelled: true` ya da `code: '1'`.
 * PaymentRequest (TWA): `AbortError`.
 */
export function isUserCancelledPurchase(error: unknown): boolean {
  const e = asErrorLike(error)
  if (!e) return false
  if (e.userCancelled === true) return true
  if (RC_PURCHASE_CANCELLED_CODES.includes(String(e.code))) return true
  return e.name === 'AbortError'
}

/** Ortam Google Play ödeme yöntemini desteklemiyor mu? (Edge: OperationError) */
export function isPaymentMethodUnsupported(error: unknown): boolean {
  const e = asErrorLike(error)
  if (!e) return false
  if (e.name === 'OperationError' || e.name === 'NotSupportedError') return true
  return typeof e.message === 'string' && /unsupported payment method/i.test(e.message)
}

export const STORE_LINKS = {
  googlePlay: 'https://play.google.com/store/apps/details?id=com.gunumvar.app',
  appStore: 'https://apps.apple.com/app/id6761272020',
} as const

export type StoreKey = keyof typeof STORE_LINKS

/** iPadOS masaüstü sitesi isterken kendini Mac olarak tanıtır; dokunmatik nokta sayısı ele verir. */
const IPAD_MIN_TOUCH_POINTS = 2

/**
 * Tarayıcının çalıştığı cihaza göre mağaza. iPhone/iPad → App Store, Android → Google Play.
 * Masaüstünde null: hangi telefonu kullandığı bilinmediği için iki mağaza da gösterilir.
 */
export function storeForDevice(userAgent: string, platform = '', maxTouchPoints = 0): StoreKey | null {
  if (/iPad|iPhone|iPod/.test(userAgent)) return 'appStore'
  if (platform === 'MacIntel' && maxTouchPoints >= IPAD_MIN_TOUCH_POINTS) return 'appStore'
  if (/Android/i.test(userAgent)) return 'googlePlay'
  return null
}

/** Tarayıcı ortamında `storeForDevice` kısa yolu. */
export function detectStoreForDevice(): StoreKey | null {
  if (typeof navigator === 'undefined') return null
  return storeForDevice(navigator.userAgent, navigator.platform, navigator.maxTouchPoints)
}

export const WEB_PURCHASE_MESSAGE =
  "Premium'u satın almak için uygulamayı App Store veya Google Play'den indirin."
